import { useState, useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, useCampaign, useCampaignAccess, useAssistant, useDataExport, useEntityLinks, usePlayDashboard } from './hooks';
import { useGlobalSearch } from './hooks/useGlobalSearch';
import { useUndoRedo } from './hooks/useUndoRedo';
import { useTheme } from './hooks/useTheme';
import { useFeatureFlags } from './hooks/useFeatureFlags';
import { NavigationProvider, useNavigation } from './contexts/NavigationContext';
import QuickAddModal from './components/QuickAddModal';
import QuickAddFAB from './components/QuickAddFAB';
import type { SuggestedItem } from './types/ui';
import type { CollectionKey, CollectionItem, NewCollectionItem, WikiArticle } from '../shared/types';
import AuthCard from './components/auth/AuthCard';
import ItemCard from './components/cards/ItemCard';
import GlobalSearch from './components/GlobalSearch';
import AppHeader from './components/layout/AppHeader';
import StatusBar from './components/layout/StatusBar';
import WorkspaceSidebar, { addRecentTab } from './components/layout/WorkspaceSidebar';
import AiFAB from './components/layout/AiFAB';
import MobileWorkspaceSwitcher from './components/layout/MobileWorkspaceSwitcher';
import ToastStack, { type ToastMessage, type ToastTone } from './components/ui/ToastStack';
import { TabLoadingSkeleton } from './components/ui/Skeleton';
import TabTransition from './components/ui/TabTransition';
import ShortcutsModal from './components/ui/ShortcutsModal';
import { useScrollLock } from './hooks/useScrollLock';
import { useModificationTracker } from './hooks/useModificationTracker';
import { useSaveIndicator } from './hooks/useSaveIndicator';
import { useOracleStream } from './hooks/useOracleStream';

// Lazy load tab components for code splitting
const OverviewTab = lazy(() => import('./components/tabs/OverviewTab'));
const OracleTab = lazy(() => import('./components/tabs/OracleTab'));
const HeatTab = lazy(() => import('./components/tabs/HeatTab'));
const CharactersTab = lazy(() => import('./components/tabs/CharactersTab'));
const NPCsTab = lazy(() => import('./components/tabs/NPCsTab'));
const LocationsTab = lazy(() => import('./components/tabs/LocationsTab'));
const OrganizationsTab = lazy(() => import('./components/tabs/OrganizationsTab'));
const PlotsTab = lazy(() => import('./components/tabs/PlotsTab'));
const SessionsTab = lazy(() => import('./components/tabs/SessionsTab'));
const CalendarTab = lazy(() => import('./components/tabs/CalendarTab'));
const MapsTab = lazy(() => import('./components/tabs/MapsTab'));
const TimelinesTab = lazy(() => import('./components/tabs/TimelinesTab'));
const GeneratorsTab = lazy(() => import('./components/tabs/GeneratorsTab'));
const WhiteboardsTab = lazy(() => import('./components/tabs/WhiteboardsTab'));
const ContentTreesTab = lazy(() => import('./components/tabs/ContentTreesTab'));
const PlayTab = lazy(() => import('./components/tabs/PlayTab'));
const RulesTab = lazy(() => import('./components/tabs/RulesTab'));
const CharacterSheetsTab = lazy(() => import('./components/tabs/CharacterSheetsTab'));
const EncounterBuilderTab = lazy(() => import('./components/tabs/EncounterBuilderTab'));
const PublishingTab = lazy(() => import('./components/tabs/PublishingTab'));
const AssistantMemoryTab = lazy(() => import('./components/tabs/AssistantMemoryTab'));
const IntegrationsTab = lazy(() => import('./components/tabs/IntegrationsTab'));
const AnalyticsTab = lazy(() => import('./components/tabs/AnalyticsTab'));
const GraphTab = lazy(() => import('./components/tabs/GraphTab'));
const FutureTab = lazy(() => import('./components/tabs/FutureTab'));
const DataTab = lazy(() => import('./components/tabs/DataTab'));
const WikiTab = lazy(() => import('./components/tabs/WikiTab'));
const AssistantTab = lazy(() => import('./components/tabs/AssistantTab'));

function TabLoadingFallback() {
  return <TabLoadingSkeleton />;
}

function NavigationBridge({
  onReady,
}: {
  onReady: (navigateTo: (name: string) => void) => void;
}) {
  const navigation = useNavigation();

  useEffect(() => {
    onReady((name: string) => {
      navigation?.navigateTo(name);
    });
  }, [navigation, onReady]);

  return null;
}

const DENSITY_STORAGE_KEY = 'campaign-tracker-density-mode';

type DensityMode = 'comfortable' | 'compact';

function getTabLabel(tab: string): string {
  if (tab === 'assistant') return 'Assistant';
  if (tab === 'oracle') return 'Oracle';
  if (tab === 'heat') return 'Heat';
  if (tab === 'data') return 'Archive';
  if (tab === 'play') return 'Session';
  if (tab === 'wiki') return 'Codex';
  if (tab === 'graph') return 'Graph';
  if (tab === 'calendar') return 'Calendar';
  if (tab === 'maps') return 'Maps';
  if (tab === 'timelines') return 'Timelines';
  if (tab === 'generators') return 'Structures';
  if (tab === 'whiteboards') return 'Whiteboard';
  if (tab === 'content-trees') return 'Content Trees';
  if (tab === 'rules') return 'Rules';
  if (tab === 'sheets') return 'Sheets';
  if (tab === 'encounters') return 'Encounters';
  if (tab === 'publish') return 'Publish';
  if (tab === 'memory') return 'Memory';
  if (tab === 'integrations') return 'Integrations';
  if (tab === 'analytics') return 'Analytics';
  return tab.charAt(0).toUpperCase() + tab.slice(1);
}

const AI_CARD_CONTEXT_OMIT_KEYS = new Set([
  'id',
  'createdAt',
  'updatedAt',
  'relationships',
  'entityLinks',
  'typedSections',
  'linkedItems',
]);

const AI_CATEGORY_LABELS: Partial<Record<CollectionKey, string>> = {
  playerCharacters: 'Player Character',
  npcs: 'NPC',
  locations: 'Location',
  organizations: 'Organization',
  plotThreads: 'Plot Thread',
  sessionNotes: 'Session Note',
  futureContent: 'Future Content',
  calendars: 'Calendar',
  calendarEvents: 'Calendar Event',
  wikiArticles: 'Wiki Article',
};

function summarizeAiFieldValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '';
    const compact = value
      .slice(0, 4)
      .map((entry) => {
        if (typeof entry === 'string') return entry.trim();
        if (typeof entry === 'number' || typeof entry === 'boolean') return String(entry);
        if (entry && typeof entry === 'object') return JSON.stringify(entry);
        return '';
      })
      .filter(Boolean)
      .join(' | ');
    const suffix = value.length > 4 ? ` (+${value.length - 4} more)` : '';
    return `${compact}${suffix}`.trim();
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return '';
}

function buildAiContextLines(item: Record<string, unknown> & { id: number }): string[] {
  return Object.entries(item)
    .filter(([key]) => !AI_CARD_CONTEXT_OMIT_KEYS.has(key))
    .map(([key, value]) => [key, summarizeAiFieldValue(value)] as const)
    .filter(([, value]) => value.length > 0)
    .slice(0, 10)
    .map(([key, value]) => {
      const clipped = value.length > 260 ? `${value.slice(0, 257)}...` : value;
      return `- ${key}: ${clipped}`;
    });
}

export default function CampaignTracker() {
  const {
    isAuthenticated,
    currentUser,
    loginForm,
    setLoginForm,
    isRegistering,
    authError,
    isLoading,
    handleLogin,
    handleLogout: authLogout,
    toggleRegistering,
  } = useAuth();

  const {
    campaigns,
    activeCampaignId,
    activeCampaign,
    selectCampaign,
    isLoading: campaignsLoading,
    error: campaignsError,
  } = useCampaignAccess(isAuthenticated);

  const {
    campaignData,
    setCampaignData,
    reloadCampaignData,
    resetCampaignData,
    addItem,
    updateItem,
    deleteItem,
    updateCampaignInfo,
    updatePartyHeat,
    updatePCHeat,
    addFactionRelationship,
    updateFactionRenown,
    removeFactionRelationship,
    addSessionLinkedItem,
    removeSessionLinkedItem,
  } = useCampaign(currentUser, isAuthenticated, activeCampaignId);

  const { tab: urlTab } = useParams<{ tab?: string }>();
  const routerNavigate = useNavigate();

  const navigateToRef = useRef<(name: string) => void>(() => {});
  const setNavigationHandler = useCallback((navigateTo: (name: string) => void) => {
    navigateToRef.current = navigateTo;
  }, []);

  const activeTab = urlTab || 'overview';
  const setActiveTab = useCallback((tab: string) => {
    routerNavigate(tab === 'overview' ? '/' : `/${tab}`, { replace: false });
  }, [routerNavigate]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState<string | null>(null);
  const [showFactionForms, setShowFactionForms] = useState<Record<number, boolean>>({});
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [densityMode, setDensityMode] = useState<DensityMode>(() => {
    if (typeof window === 'undefined') return 'comfortable';
    return window.localStorage.getItem(DENSITY_STORAGE_KEY) === 'compact'
      ? 'compact'
      : 'comfortable';
  });
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('sidebar-collapsed') === 'true';
  });

  // Lock body scroll when any overlay is open
  useScrollLock(isSearchOpen || isQuickAddOpen || isShortcutsOpen);

  const { recentActivity } = useModificationTracker();
  const { saveStatus, debouncedSave } = useSaveIndicator();

  const {
    refreshFeatureFlags,
    tabs,
    campaignRunEnabled,
    statblocksEnabled,
    rulesCompendiumEnabled,
    characterSheetsEnabled,
    encounterBuilderEnabled,
    publishingEnabled,
    assistantMemoryEnabled,
    integrationsEnabled,
    familyTreesEnabled,
    diplomacyWebsEnabled,
    whiteboardsEnabled,
    contentTreesEnabled,
  } = useFeatureFlags(isAuthenticated, activeCampaignId);

  const showToast = useCallback((message: string, tone: ToastTone = 'info') => {
    const id = Date.now() + Math.floor(Math.random() * 10000);
    setToasts((prev) => [...prev, { id, message, tone }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 4200);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    window.localStorage.setItem(DENSITY_STORAGE_KEY, densityMode);
  }, [densityMode]);

  useEffect(() => {
    window.localStorage.setItem('sidebar-collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const showToastRef = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast; }, [showToast]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleOnline = () => {
      setIsOnline(true);
      // Replay offline mutation queue on reconnect
      import('./utils/offlineQueue').then(({ replayQueue }) =>
        replayQueue().then(({ replayed, failed }: { replayed: number; failed: number }) => {
          if (replayed > 0 || failed > 0) {
            showToastRef.current(`Back online: ${replayed} change(s) synced${failed > 0 ? `, ${failed} failed` : ''}.`, replayed > 0 && failed === 0 ? 'success' : 'info');
          }
        }).catch(() => { /* queue replay best-effort */ })
      ).catch(() => { /* dynamic import best-effort */ });
    };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const campaignDataRef = useRef(campaignData);
  useEffect(() => {
    campaignDataRef.current = campaignData;
  }, [campaignData]);

  const {
    chatMessages,
    chatInput,
    setChatInput,
    isChatLoading,
    isOnline: isAssistantOnline,
    offlineQueue,
    isQueueSyncing,
    queueSyncError,
    lastQueueSyncAt,
    suggestedItems,
    sendChatMessage,
    clearChat,
    removeSuggestion,
    quickActions,
    sendQuickAction,
    followUpSuggestions,
    sendFollowUp,
    editingSuggestion,
    startEditingSuggestion,
    updateEditingSuggestion,
    saveEditedSuggestion,
    cancelEditingSuggestion,
    syncOfflineQueue,
    removeQueuedPrompt,
    clearOfflineQueue,
    exportChat,
    pendingOperations,
    executedOperations,
    autoExecuteMode,
    setAutoExecuteMode,
    approveOperation,
    approveAllOperations,
    rejectOperation,
    rejectAllOperations,
    undoOperation,
    clearExecutedOperations,
    contextPins,
    addContextPin,
    removeContextPin,
    clearAllContextPins,
  } = useAssistant(campaignData, activeTab, reloadCampaignData);

  // Handle Assistant Actions
  const handleAssistantAction = useCallback((item: SuggestedItem) => {
      if (item.type === 'action_navigate') {
          if (item.entity) {
              navigateToRef.current(item.entity);
          } else if (item.target) {
              setActiveTab(item.target);
          }
      } else if (item.type === 'action_quick_add') {
          setIsQuickAddOpen(true);
          // Ideally switch tab in quick add, but modal handles its own state currently.
          // We can just open the modal for now.
      } else if (item.type === 'action_update_heat') {
          updatePartyHeat(String(item.value));
      }
      // Auto-remove action items after execution?
      // Maybe let the user click "Execute"?
      // For "more agentic", let's execute immediately?
      // But we are in a render loop potentially if we just call it.
      // We need a specific "Execute" button in the UI for safety, or auto-execute if carefully managed.
      // Let's stick to "Click to Execute" or visual buttons for now.
  }, [setActiveTab, setIsQuickAddOpen, updatePartyHeat]);

  const askAiForItem = useCallback(
    (item: Record<string, unknown> & { id: number }, category: CollectionKey) => {
      const categoryLabel = AI_CATEGORY_LABELS[category] || category;
      const contextLines = buildAiContextLines(item);
      const contextBlock = contextLines.length > 0
        ? contextLines.join('\n')
        : '- (no structured fields provided)';

      const prompt = `I need help refining this ${categoryLabel} in my campaign.

${contextBlock}

Please do all of the following:
1. Flag any continuity issues, unclear details, or missing links.
2. Suggest 3 concrete improvements I can apply immediately.
3. Provide one "ready-to-paste" improved version that keeps canon consistent.

If useful, emit structured tags like [NPC], [LOCATION], [ORGANIZATION], [PLOT], [FUTURE], [WIKI], or [SESSION].`;

      setActiveTab('assistant');
      void sendChatMessage(prompt);
    },
    [sendChatMessage, setActiveTab]
  );

  const askAiForWikiArticle = useCallback(
    (article: WikiArticle) => {
      askAiForItem(article as Record<string, unknown> & { id: number }, 'wikiArticles');
    },
    [askAiForItem]
  );

  const askAiForPlayerCharacter = useCallback(
    (pc: Record<string, unknown> & { id: number }) => {
      askAiForItem(pc, 'playerCharacters');
    },
    [askAiForItem]
  );

  const {
    exportAllData,
    importData,
    getJSONPreview,
  } = useDataExport(currentUser, campaignData, setCampaignData, showToast);

  const { theme, toggleTheme } = useTheme();

  const {
    blocks: oracleBlocks,
    suggestions: oracleSuggestions,
    isStreaming: oracleIsStreaming,
    error: oracleError,
    generate: oracleGenerate,
    cancel: oracleCancel,
    clear: oracleClear,
  } = useOracleStream();

  const {
    pinnedItems,
    pinItem,
    unpinItem,
    diceHistory,
    addDiceRoll,
    clearDiceHistory,
  } = usePlayDashboard();

  const {
    setExpandedCard,
    resolveItemLinks,
    getNpcNamesForLocation,
    openOrganizationCard,
    openEntityLink,
    getWikiLinksForItem,
    isCardExpanded,
    handleExpandedChange,
    expandedOrganization,
    expandedLocation,
    expandedNpc,
  } = useEntityLinks(campaignData);

  const {
    pushAction,
    undo,
    redo,
    canUndo,
    canRedo,
    lastAction,
    nextRedoAction,
  } = useUndoRedo();

  // Wrap deleteItem with undo functionality
  const deleteItemWithUndo = useCallback(async <K extends CollectionKey>(
    category: K,
    id: number
  ) => {
    // Store the item before deleting
    const items = campaignDataRef.current[category] as CollectionItem<K>[];
    const itemToDelete = items.find((item) => item.id === id);
    if (!itemToDelete) return;
    const itemSnapshot = { ...itemToDelete } as CollectionItem<K>;
    const factionRelationshipsToRestore =
      category === 'playerCharacters' && 'factionRelationships' in itemSnapshot
        ? [...(((itemSnapshot as { factionRelationships?: { faction: string; renown: number }[] }).factionRelationships) || [])]
        : [];
    const linkedItemsToRestore =
      category === 'sessionNotes' && 'linkedItems' in itemSnapshot
        ? [...(((itemSnapshot as { linkedItems?: { category: string; itemId: number }[] }).linkedItems) || [])]
        : [];

    const categoryLabels: Record<CollectionKey, string> = {
      playerCharacters: 'character',
      npcs: 'NPC',
      locations: 'location',
      organizations: 'organization',
      plotThreads: 'plot',
      sessionNotes: 'session',
      calendars: 'calendar',
      calendarEvents: 'event',
      futureContent: 'future content',
      wikiArticles: 'wiki article',
    };

    const itemName = 'name' in itemSnapshot
      ? String((itemSnapshot as { name?: string }).name)
      : 'characterName' in itemSnapshot
        ? String((itemSnapshot as { characterName?: string }).characterName)
        : 'title' in itemSnapshot
          ? String((itemSnapshot as { title?: string }).title)
          : `Session ${(itemSnapshot as { sessionNum?: number }).sessionNum}`;

    await deleteItem(category, id);

    let restoredId: number | null = null;

    pushAction({
      type: 'delete',
      description: `Delete ${categoryLabels[category]} "${itemName}"`,
      undo: async () => {
        // Re-add the item (the API will assign a new ID)
        const itemWithoutId = { ...(itemSnapshot as Record<string, unknown>) } as Record<string, unknown>;
        delete itemWithoutId.id;
        const created = await addItem(category, itemWithoutId as NewCollectionItem<K>);
        restoredId = created?.id ?? null;
        if (category === 'playerCharacters' && restoredId && factionRelationshipsToRestore.length > 0) {
          for (const rel of factionRelationshipsToRestore) {
            await addFactionRelationship(restoredId, rel.faction, rel.renown);
          }
        }
        if (category === 'sessionNotes' && restoredId && linkedItemsToRestore.length > 0) {
          for (const link of linkedItemsToRestore) {
            await addSessionLinkedItem(restoredId, link.category, link.itemId);
          }
        }
      },
      redo: async () => {
        // Find and delete the restored item by matching properties
        const currentItems = campaignDataRef.current[category] as CollectionItem<K>[];
        const restoredItem = restoredId
          ? currentItems.find((item) => item.id === restoredId)
          : currentItems.find((item) => {
              if ('name' in item && 'name' in itemSnapshot) {
                return (item as { name?: string }).name === (itemSnapshot as { name?: string }).name;
              }
              if ('characterName' in item && 'characterName' in itemSnapshot) {
                return (item as { characterName?: string }).characterName === (itemSnapshot as { characterName?: string }).characterName;
              }
              if ('title' in item && 'title' in itemSnapshot) {
                return (item as { title?: string }).title === (itemSnapshot as { title?: string }).title;
              }
              if ('sessionNum' in item && 'sessionNum' in itemSnapshot) {
                return (item as { sessionNum?: number }).sessionNum === (itemSnapshot as { sessionNum?: number }).sessionNum
                  && String((item as { date?: string }).date || '') === String((itemSnapshot as { date?: string }).date || '')
                  && String((item as { summary?: string }).summary || '') === String((itemSnapshot as { summary?: string }).summary || '');
              }
              return false;
            });
        if (restoredItem) {
          await deleteItem(category, restoredItem.id);
        }
      },
    });
  }, [addFactionRelationship, addItem, addSessionLinkedItem, deleteItem, pushAction]);

  const {
    query: globalSearchQuery,
    setQuery: setGlobalSearchQuery,
    results: globalSearchResults,
  } = useGlobalSearch(campaignData);

  const handleGlobalSearchSelect = useCallback((category: CollectionKey, id: number) => {
    const tabMap: Record<CollectionKey, string> = {
      playerCharacters: 'characters',
      npcs: 'npcs',
      locations: 'locations',
      organizations: 'organizations',
      plotThreads: 'plots',
      sessionNotes: 'sessions',
      calendars: 'calendar',
      calendarEvents: 'calendar',
      futureContent: 'future',
      wikiArticles: 'wiki',
    };
    setActiveTab(tabMap[category]);
    setExpandedCard({ category, id });
    setIsSearchOpen(false);
    setIsQuickAddOpen(false); // Close Quick Add if open (though modal shouldn't allow this)
    setGlobalSearchQuery('');
  }, [setExpandedCard, setGlobalSearchQuery]);

  const handleLinkNavigate = useCallback((tab: string, query?: string) => {
    if (!query) return;
    if (tab === 'wiki') {
      setActiveTab('wiki');
      setSearchTerm(query);
      setExpandedCard(null);
      return;
    }
    if (tab === 'organizations') {
      setActiveTab('organizations');
      setSearchTerm(query);
      setExpandedCard(null);
      return;
    }
    if (tab === 'plotThreads') {
      setActiveTab('plots');
      setSearchTerm(query);
      setExpandedCard(null);
      return;
    }
    openEntityLink(tab, query);
  }, [openEntityLink, setActiveTab, setExpandedCard, setSearchTerm]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K: Global Search
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsSearchOpen(true);
      }
      // Cmd/Ctrl + I: Quick Add
      if (e.key === 'i' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsQuickAddOpen(true);
      }
      // Cmd/Ctrl + J: Navigate to Assistant tab
      if (e.key === 'j' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setActiveTab('assistant');
        addRecentTab('assistant');
      }
      // ?: Keyboard shortcuts help (only when not typing in an input)
      if (e.key === '?' && !e.metaKey && !e.ctrlKey) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') {
          e.preventDefault();
          setIsShortcutsOpen(true);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = () => {
    authLogout();
    resetCampaignData();
  };

  const toggleDensityMode = useCallback(() => {
    setDensityMode((prev) => (prev === 'comfortable' ? 'compact' : 'comfortable'));
  }, []);

  const updateCampaignInfoWithIndicator = useCallback(
    (field: Parameters<typeof updateCampaignInfo>[0], value: Parameters<typeof updateCampaignInfo>[1]) => {
      // Update local state immediately via the original function
      updateCampaignInfo(field, value);
      // Show save indicator (the actual API call is already handled by updateCampaignInfo)
      debouncedSave(() => { /* API save already triggered */ });
    },
    [updateCampaignInfo, debouncedSave]
  );

  const handleTabChange = (tab: string) => {
    if (!tabs.includes(tab)) return;
    setActiveTab(tab);
    addRecentTab(tab);
    setSearchTerm('');
    setExpandedCard(null);
    setShowAddForm(null);
  };

  const effectiveActiveTab = tabs.includes(activeTab) ? activeTab : 'overview';

  const filterItems = useCallback(<T,>(items: T[]) => {
    if (!items || !Array.isArray(items)) return [] as T[];
    if (!searchTerm) return items;
    return items.filter((item) =>
      Object.values(item as Record<string, unknown>).some((val) =>
        String(val).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm]);

  const addSuggestedItem = (suggestion: SuggestedItem, index: number) => {
    if (suggestion.type === 'pc') {
      const initialRelationships = suggestion.faction
        ? [{ id: Date.now(), faction: suggestion.faction, renown: 1 }]
        : [];
      addItem('playerCharacters', {
        characterName: suggestion.characterName,
        playerName: suggestion.playerName,
        class: suggestion.class,
        level: suggestion.level,
        faction: suggestion.faction,
        personalHeat: suggestion.personalHeat || 0,
        heatNotes: suggestion.heatNotes || '',
        factionRelationships: initialRelationships,
        notes: suggestion.notes
      });
    } else if (suggestion.type === 'npc') {
      addItem('npcs', {
        name: suggestion.name,
        description: suggestion.description,
        location: suggestion.location,
        notes: suggestion.notes
      });
    } else if (suggestion.type === 'location') {
      addItem('locations', {
        name: suggestion.name,
        description: suggestion.description,
        connections: suggestion.connections,
        notes: suggestion.notes
      });
    } else if (suggestion.type === 'organization') {
      addItem('organizations', {
        name: suggestion.name,
        type: suggestion.type_value,
        goals: suggestion.goals,
        members: suggestion.members,
        notes: suggestion.notes
      });
    } else if (suggestion.type === 'plot') {
      addItem('plotThreads', {
        title: suggestion.title,
        description: suggestion.description,
        status: suggestion.status,
        priority: suggestion.priority
      });
    } else if (suggestion.type === 'future') {
      addItem('futureContent', {
        title: suggestion.title,
        type: suggestion.type_value,
        description: suggestion.description,
        prepNotes: suggestion.prepNotes
      });
    } else if (suggestion.type === 'wiki') {
      const normalizedTags = suggestion.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
      const visibility = ['public', 'players', 'gm', 'share_group'].includes(suggestion.visibility)
        ? suggestion.visibility
        : 'public';
      addItem('wikiArticles', {
        title: suggestion.title,
        type: suggestion.articleType,
        summary: suggestion.summary,
        content: suggestion.content,
        visibility,
        tags: normalizedTags,
        parentId: null,
        templateType: '',
        templateData: {},
        relationships: [],
        entityLinks: [],
      });
    } else if (suggestion.type === 'session') {
      addItem('sessionNotes', {
        sessionNum: suggestion.sessionNum,
        date: suggestion.date,
        summary: suggestion.summary,
        highlights: suggestion.highlights,
      });
    } else if (suggestion.type === 'session_link') {
      // Find the session note by sessionNum
      const session = campaignData.sessionNotes.find(
        (sn) => sn.sessionNum === suggestion.sessionNum
      );
      if (!session) {
        showToast(`Session ${suggestion.sessionNum} not found. Create the session first.`, 'error');
        return;
      }
      // Find the item by name in the category
      const catItems = campaignData[suggestion.category] as Array<Record<string, unknown>>;
      const nameKey = suggestion.category === 'plotThreads' ? 'title' : 'name';
      const target = catItems?.find(
        (ci) => String(ci[nameKey]).toLowerCase() === suggestion.itemName.toLowerCase()
      );
      if (!target) {
        showToast(`"${suggestion.itemName}" not found in ${suggestion.category}. Add the item first.`, 'error');
        return;
      }
      // Check if already linked
      const alreadyLinked = (session.linkedItems || []).some(
        (li) => li.category === suggestion.category && li.itemId === (target.id as number)
      );
      if (alreadyLinked) {
        showToast(`"${suggestion.itemName}" is already linked to Session ${suggestion.sessionNum}.`, 'info');
      } else {
        addSessionLinkedItem(session.id, suggestion.category, target.id as number);
        showToast(`Linked "${suggestion.itemName}" to Session ${suggestion.sessionNum}.`, 'success');
      }
    }
    removeSuggestion(index);
  };

  const handleAiDockPrompt = useCallback((prompt: string) => {
    if (!prompt.trim()) return;
    setActiveTab('assistant');
    void sendChatMessage(prompt.trim());
  }, [sendChatMessage, setActiveTab]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-slate-700">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <AuthCard
        isRegistering={isRegistering}
        loginForm={loginForm}
        setLoginForm={setLoginForm}
        authError={authError}
        onSubmit={handleLogin}
        onToggleMode={toggleRegistering}
      />
    );
  }

  return (
    <NavigationProvider campaignData={campaignData} onNavigate={handleGlobalSearchSelect}>
    <NavigationBridge onReady={setNavigationHandler} />
    <a href="#main-content" className="skip-link">Skip to main content</a>
    <div className="min-h-screen theme-clean" data-density={densityMode}>
      <div className="page-container pb-10">
        <AppHeader
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={undo}
          onRedo={redo}
          lastActionDescription={lastAction?.description}
          nextRedoDescription={nextRedoAction?.description}
          campaigns={campaigns}
          activeCampaignId={activeCampaignId}
          campaignsLoading={campaignsLoading}
          campaignsError={campaignsError}
          selectCampaign={(id: number) => {
            selectCampaign(id);
            const name = campaigns.find((c) => c.id === id)?.name;
            if (name) showToast(`Switched to "${name}"`, 'info');
          }}
          onOpenSearch={() => setIsSearchOpen(true)}
          densityMode={densityMode}
          onToggleDensity={toggleDensityMode}
          theme={theme}
          onToggleTheme={toggleTheme}
          username={currentUser?.username || ''}
          onLogout={handleLogout}
          activeTabLabel={getTabLabel(effectiveActiveTab)}
        />

        {!isOnline && (
          <div className="offline-banner" role="status">
            Offline mode active. You can continue editing local context; cloud sync and AI requests will resume after reconnect.
            {offlineQueue.length > 0
              ? ` ${offlineQueue.length} assistant prompt${offlineQueue.length === 1 ? '' : 's'} queued.`
              : ''}
          </div>
        )}

        <div className={`workspace-layout ${sidebarCollapsed ? 'workspace-layout-collapsed' : ''}`}>
          <WorkspaceSidebar
            tabs={tabs}
            activeTab={effectiveActiveTab}
            getTabLabel={getTabLabel}
            onTabChange={handleTabChange}
            onOpenSearch={() => setIsSearchOpen(true)}
            onOpenQuickAdd={() => setIsQuickAddOpen(true)}
            isCollapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
          />

          <div className="surface-card rounded-2xl">
          <MobileWorkspaceSwitcher
            tabs={tabs}
            activeTab={effectiveActiveTab}
            getTabLabel={getTabLabel}
            onTabChange={handleTabChange}
          />

          <div id="main-content" className="p-6 relative">
            {saveStatus !== 'idle' && (
              <div className="save-indicator">
                {saveStatus === 'saving' && (
                  <span className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
                    <span className="inline-block w-3 h-3 border-2 border-[var(--accent-glow)] border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </span>
                )}
                {saveStatus === 'saved' && (
                  <span className="text-xs text-green-600">&#10003; Saved</span>
                )}
                {saveStatus === 'error' && (
                  <span className="text-xs text-red-600">Save failed</span>
                )}
              </div>
            )}
            <Suspense fallback={<TabLoadingFallback />}>
            <TabTransition tabKey={effectiveActiveTab}>
            {effectiveActiveTab === 'overview' && (
              <OverviewTab
                campaignData={campaignData}
                updateCampaignInfo={updateCampaignInfoWithIndicator}
                onNavigate={handleTabChange}
                recentActivity={recentActivity}
                showToast={showToast}
              />
            )}

            {effectiveActiveTab === 'oracle' && (
              <OracleTab
                campaignData={campaignData}
                activeCampaignId={activeCampaignId}
                onNavigate={handleLinkNavigate}
                onOpenEntity={handleGlobalSearchSelect}
                onSendPromptToAssistant={handleAiDockPrompt}
                showToast={showToast}
                oracleBlocks={oracleBlocks}
                oracleSuggestions={oracleSuggestions}
                oracleIsStreaming={oracleIsStreaming}
                oracleError={oracleError}
                oracleGenerate={oracleGenerate}
                oracleCancel={oracleCancel}
                oracleClear={oracleClear}
              />
            )}

            {effectiveActiveTab === 'assistant' && (
              <AssistantTab
                campaignData={campaignData}
                chatMessages={chatMessages}
                chatInput={chatInput}
                setChatInput={setChatInput}
                isChatLoading={isChatLoading}
                sendChatMessage={sendChatMessage}
                clearChat={clearChat}
                suggestedItems={suggestedItems}
                addSuggestedItem={addSuggestedItem}
                quickActions={quickActions}
                sendQuickAction={sendQuickAction}
                followUpSuggestions={followUpSuggestions}
                sendFollowUp={sendFollowUp}
                editingSuggestion={editingSuggestion}
                startEditingSuggestion={startEditingSuggestion}
                updateEditingSuggestion={updateEditingSuggestion}
                saveEditedSuggestion={saveEditedSuggestion}
                cancelEditingSuggestion={cancelEditingSuggestion}
                removeSuggestion={removeSuggestion}
                onPinItem={pinItem}
                onExecuteAction={handleAssistantAction}
                pendingOperations={pendingOperations}
                executedOperations={executedOperations}
                autoExecuteMode={autoExecuteMode}
                onToggleAutoExecute={setAutoExecuteMode}
                onApproveOperation={approveOperation}
                onApproveAllOperations={approveAllOperations}
                onRejectOperation={rejectOperation}
                onRejectAllOperations={rejectAllOperations}
                onUndoOperation={undoOperation}
                onClearExecutedOperations={clearExecutedOperations}
                onNavigate={handleTabChange}
                contextPins={contextPins}
                onAddContextPin={addContextPin}
                onRemoveContextPin={removeContextPin}
                onClearAllContextPins={clearAllContextPins}
              />
            )}

            {effectiveActiveTab === 'play' && (
              <PlayTab 
                campaignData={campaignData} 
                campaignRole={activeCampaign?.role ?? null}
                campaignRunEnabled={campaignRunEnabled}
                statblocksEnabled={statblocksEnabled}
                showToast={showToast}
                pinnedItems={pinnedItems}
                pinItem={pinItem}
                unpinItem={unpinItem}
                diceHistory={diceHistory}
                addDiceRoll={addDiceRoll}
                clearDiceHistory={clearDiceHistory}
              />
            )}

            {effectiveActiveTab === 'rules' && (
              <RulesTab
                enabled={rulesCompendiumEnabled}
                campaignRole={activeCampaign?.role ?? null}
                showToast={showToast}
              />
            )}

            {effectiveActiveTab === 'sheets' && (
              <CharacterSheetsTab
                enabled={characterSheetsEnabled}
                campaignRole={activeCampaign?.role ?? null}
                showToast={showToast}
              />
            )}

            {effectiveActiveTab === 'encounters' && (
              <EncounterBuilderTab
                enabled={encounterBuilderEnabled}
                campaignRole={activeCampaign?.role ?? null}
                showToast={showToast}
                onNavigateToPlay={() => setActiveTab('play')}
              />
            )}

            {effectiveActiveTab === 'publish' && (
              <PublishingTab
                enabled={publishingEnabled}
                campaignRole={activeCampaign?.role ?? null}
                showToast={showToast}
              />
            )}

            {effectiveActiveTab === 'memory' && (
              <AssistantMemoryTab
                enabled={assistantMemoryEnabled}
                campaignRole={activeCampaign?.role ?? null}
                showToast={showToast}
              />
            )}

            {effectiveActiveTab === 'integrations' && (
              <IntegrationsTab
                enabled={integrationsEnabled}
                campaignRole={activeCampaign?.role ?? null}
                showToast={showToast}
              />
            )}

            {effectiveActiveTab === 'analytics' && (
              <AnalyticsTab
                campaignData={campaignData}
                onOpenEntity={handleGlobalSearchSelect}
                showToast={showToast}
              />
            )}

            {effectiveActiveTab === 'heat' && (
              <HeatTab
                campaignData={campaignData}
                updateCampaignInfo={updateCampaignInfo}
                updatePartyHeat={updatePartyHeat}
                updatePCHeat={updatePCHeat}
                updateItem={updateItem}
              />
            )}

            {effectiveActiveTab === 'characters' && (
              <CharactersTab
                campaignData={campaignData}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                showAddForm={showAddForm}
                setShowAddForm={setShowAddForm}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItemWithUndo}
                updatePCHeat={updatePCHeat}
                addFactionRelationship={addFactionRelationship}
                updateFactionRenown={updateFactionRenown}
                removeFactionRelationship={removeFactionRelationship}
                showFactionForms={showFactionForms}
                setShowFactionForms={setShowFactionForms}
                onOpenFaction={openOrganizationCard}
                filterItems={filterItems}
                onAskAiForCharacter={askAiForPlayerCharacter}
              />
            )}

            {effectiveActiveTab === 'npcs' && (
              <NPCsTab
                campaignData={campaignData}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                showAddForm={showAddForm}
                setShowAddForm={setShowAddForm}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItemWithUndo}
                filterItems={filterItems}
                isCardExpanded={isCardExpanded}
                handleExpandedChange={handleExpandedChange}
                resolveItemLinks={resolveItemLinks}
                onNavigate={handleLinkNavigate}
                getWikiLinksForItem={getWikiLinksForItem}
                onAskAiForItem={askAiForItem}
              />
            )}

            {effectiveActiveTab === 'locations' && (
              <LocationsTab
                campaignData={campaignData}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                showAddForm={showAddForm}
                setShowAddForm={setShowAddForm}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItemWithUndo}
                filterItems={filterItems}
                isCardExpanded={isCardExpanded}
                handleExpandedChange={handleExpandedChange}
                resolveItemLinks={resolveItemLinks}
                onNavigate={handleLinkNavigate}
                getNpcNamesForLocation={getNpcNamesForLocation}
                getWikiLinksForItem={getWikiLinksForItem}
                onAskAiForItem={askAiForItem}
              />
            )}

            {effectiveActiveTab === 'organizations' && (
              <OrganizationsTab
                campaignData={campaignData}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                showAddForm={showAddForm}
                setShowAddForm={setShowAddForm}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItemWithUndo}
                filterItems={filterItems}
                isCardExpanded={isCardExpanded}
                handleExpandedChange={handleExpandedChange}
                resolveItemLinks={resolveItemLinks}
                onNavigate={handleLinkNavigate}
                getWikiLinksForItem={getWikiLinksForItem}
                onAskAiForItem={askAiForItem}
              />
            )}

            {effectiveActiveTab === 'plots' && (
              <PlotsTab
                campaignData={campaignData}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                showAddForm={showAddForm}
                setShowAddForm={setShowAddForm}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItemWithUndo}
                filterItems={filterItems}
                isCardExpanded={isCardExpanded}
                handleExpandedChange={handleExpandedChange}
                resolveItemLinks={resolveItemLinks}
                onNavigate={handleLinkNavigate}
                getWikiLinksForItem={getWikiLinksForItem}
                onAskAiForItem={askAiForItem}
              />
            )}

            {effectiveActiveTab === 'sessions' && (
              <SessionsTab
                campaignData={campaignData}
                showAddForm={showAddForm}
                setShowAddForm={setShowAddForm}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItemWithUndo}
                updateCampaignInfo={updateCampaignInfo}
                isCardExpanded={isCardExpanded}
                handleExpandedChange={handleExpandedChange}
                addSessionLinkedItem={addSessionLinkedItem}
                removeSessionLinkedItem={removeSessionLinkedItem}
                campaignRole={activeCampaign?.role ?? null}
                reloadCampaignData={reloadCampaignData}
                onAskAiForItem={askAiForItem}
              />
            )}

            {effectiveActiveTab === 'calendar' && (
              <CalendarTab
                campaignData={campaignData}
                showAddForm={showAddForm}
                setShowAddForm={setShowAddForm}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItemWithUndo}
                isCardExpanded={isCardExpanded}
                handleExpandedChange={handleExpandedChange}
                filterItems={filterItems}
                onAskAiForItem={askAiForItem}
              />
            )}

            {effectiveActiveTab === 'maps' && (
              <MapsTab
                campaignData={campaignData}
                setCampaignData={setCampaignData}
                campaignRole={activeCampaign?.role ?? null}
                showToast={showToast}
              />
            )}

            {effectiveActiveTab === 'timelines' && (
              <TimelinesTab
                campaignData={campaignData}
              />
            )}

            {effectiveActiveTab === 'generators' && (
              <GeneratorsTab
                campaignData={campaignData}
                campaignRole={activeCampaign?.role ?? null}
                familyTreesEnabled={familyTreesEnabled}
                diplomacyWebsEnabled={diplomacyWebsEnabled}
                showToast={showToast}
              />
            )}

            {effectiveActiveTab === 'whiteboards' && (
              <WhiteboardsTab
                campaignData={campaignData}
                campaignRole={activeCampaign?.role ?? null}
                whiteboardsEnabled={whiteboardsEnabled}
                showToast={showToast}
              />
            )}

            {effectiveActiveTab === 'content-trees' && (
              <ContentTreesTab
                campaignData={campaignData}
                campaignRole={activeCampaign?.role ?? null}
                contentTreesEnabled={contentTreesEnabled}
                showToast={showToast}
              />
            )}

            {effectiveActiveTab === 'wiki' && (
              <WikiTab
                campaignData={campaignData}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                showAddForm={showAddForm}
                setShowAddForm={setShowAddForm}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItemWithUndo}
                filterItems={filterItems}
                campaignRole={activeCampaign?.role ?? null}
                reloadCampaignData={reloadCampaignData}
                onAskAiForArticle={askAiForWikiArticle}
                onOpenEntity={handleGlobalSearchSelect}
              />
            )}

            {effectiveActiveTab === 'future' && (
              <FutureTab
                campaignData={campaignData}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                showAddForm={showAddForm}
                setShowAddForm={setShowAddForm}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItemWithUndo}
                filterItems={filterItems}
                isCardExpanded={isCardExpanded}
                handleExpandedChange={handleExpandedChange}
                onAskAiForItem={askAiForItem}
              />
            )}

            {effectiveActiveTab === 'graph' && (
              <GraphTab
                campaignData={campaignData}
                onOpenEntity={handleGlobalSearchSelect}
                showToast={showToast}
              />
            )}

            {effectiveActiveTab === 'data' && (
              <DataTab
                exportAllData={exportAllData}
                importData={importData}
                getJSONPreview={getJSONPreview}
                activeCampaignId={activeCampaignId}
                campaignRole={activeCampaign?.role ?? null}
                reloadCampaignData={reloadCampaignData}
                refreshFeatureFlags={refreshFeatureFlags}
                showToast={showToast}
                assistantQueueItems={offlineQueue}
                assistantQueueIsOnline={isAssistantOnline}
                assistantQueueSyncing={isQueueSyncing}
                assistantQueueSyncError={queueSyncError}
                assistantQueueLastSyncedAt={lastQueueSyncAt}
                onSyncAssistantQueue={syncOfflineQueue}
                onRemoveAssistantQueueItem={removeQueuedPrompt}
                onClearAssistantQueue={clearOfflineQueue}
              />
            )}
            </TabTransition>
            </Suspense>

            {expandedOrganization && effectiveActiveTab !== 'organizations' && (
              <ItemCard
                key={expandedOrganization.id}
                item={expandedOrganization}
                category="organizations"
                forceExpanded={true}
                onExpandedChange={handleExpandedChange('organizations', expandedOrganization.id)}
                fields={[
                  { key: 'name', label: 'Name' },
                  { key: 'type', label: 'Type' },
                  { key: 'goals', label: 'Goals', type: 'textarea' },
                  { key: 'members', label: 'Notable Members' },
                  { key: 'notes', label: 'Notes', type: 'textarea' }
                ]}
                updateItem={updateItem}
                deleteItem={deleteItemWithUndo}
                titleKey="name"
                subtitleKey="type"
                accent="organizations"
                onAskAi={askAiForItem}
              />
            )}

            {expandedLocation && effectiveActiveTab !== 'locations' && (
              <ItemCard
                key={expandedLocation.id}
                item={{ ...expandedLocation, associatedNpcs: getNpcNamesForLocation(expandedLocation) }}
                category="locations"
                forceExpanded={true}
                onExpandedChange={handleExpandedChange('locations', expandedLocation.id)}
                fields={[
                  { key: 'name', label: 'Name' },
                  { key: 'description', label: 'Description', type: 'textarea' },
                  { key: 'connections', label: 'Connections' },
                  { key: 'associatedNpcs', label: 'NPCs', readonly: true },
                  { key: 'notes', label: 'Notes', type: 'textarea' }
                ]}
                updateItem={updateItem}
                deleteItem={deleteItemWithUndo}
                linkResolver={resolveItemLinks}
                onNavigate={openEntityLink}
                titleKey="name"
                accent="locations"
                onAskAi={askAiForItem}
              />
            )}

            {expandedNpc && effectiveActiveTab !== 'npcs' && (
              <ItemCard
                key={expandedNpc.id}
                item={expandedNpc}
                category="npcs"
                forceExpanded={true}
                onExpandedChange={handleExpandedChange('npcs', expandedNpc.id)}
                fields={[
                  { key: 'name', label: 'Name' },
                  { key: 'description', label: 'Description', type: 'textarea' },
                  { key: 'location', label: 'Location' },
                  { key: 'notes', label: 'Notes', type: 'textarea' }
                ]}
                updateItem={updateItem}
                deleteItem={deleteItemWithUndo}
                linkResolver={resolveItemLinks}
                onNavigate={openEntityLink}
                titleKey="name"
                subtitleKey="location"
                accent="npcs"
                onAskAi={askAiForItem}
              />
            )}
          </div>
        </div>
        </div>
      </div>

      <GlobalSearch
        query={globalSearchQuery}
        setQuery={setGlobalSearchQuery}
        results={globalSearchResults}
        isOpen={isSearchOpen}
        onClose={() => {
          setIsSearchOpen(false);
          setGlobalSearchQuery('');
        }}
        onSelectResult={handleGlobalSearchSelect}
        tabs={tabs}
        activeTab={effectiveActiveTab}
        onNavigateToTab={handleTabChange}
        onOpenQuickAdd={() => setIsQuickAddOpen(true)}
        onOpenAssistant={() => handleTabChange('assistant')}
        onToggleTheme={toggleTheme}
        theme={theme}
      />
      
      <QuickAddFAB onClick={() => setIsQuickAddOpen(true)} />
      
      <QuickAddModal 
        isOpen={isQuickAddOpen}
        onClose={() => setIsQuickAddOpen(false)}
        addItem={addItem}
        campaignData={campaignData}
        updateCampaignInfo={updateCampaignInfo}
        campaignRole={activeCampaign?.role ?? null}
      />

      <AiFAB
        activeTab={effectiveActiveTab}
        activeTabLabel={getTabLabel(effectiveActiveTab)}
        isBusy={isChatLoading}
        isOffline={!isAssistantOnline}
        offlineQueueCount={offlineQueue.length}
        isQueueSyncing={isQueueSyncing}
        campaignStats={{
          npcCount: campaignData.npcs?.length ?? 0,
          locationCount: campaignData.locations?.length ?? 0,
          activePlotCount: campaignData.plotThreads?.filter((p) => p.status === 'Active').length ?? 0,
          sessionNumber: campaignData.sessionNumber,
          partyHeat: campaignData.partyHeat,
        }}
        onSyncQueue={() => {
          void syncOfflineQueue();
        }}
        onSendPrompt={handleAiDockPrompt}
        onOpenAssistant={() => handleTabChange('assistant')}
        onOpenSearch={() => setIsSearchOpen(true)}
        onOpenQuickAdd={() => setIsQuickAddOpen(true)}
        onExportChat={exportChat}
      />
    </div>
    
      <ShortcutsModal isOpen={isShortcutsOpen} onClose={() => setIsShortcutsOpen(false)} />
      <ToastStack toasts={toasts} onDismiss={dismissToast} />

      <StatusBar
        campaignName={campaignData.name || ''}
        entityCount={
          (campaignData.playerCharacters?.length ?? 0)
          + (campaignData.npcs?.length ?? 0)
          + (campaignData.locations?.length ?? 0)
          + (campaignData.organizations?.length ?? 0)
          + (campaignData.plotThreads?.length ?? 0)
          + (campaignData.sessionNotes?.length ?? 0)
          + (campaignData.wikiArticles?.length ?? 0)
        }
        activeTabLabel={getTabLabel(effectiveActiveTab)}
        saveStatus={saveStatus}
        isOnline={isOnline}
        theme={theme}
        onToggleTheme={toggleTheme}
      />

    </NavigationProvider>
  );
}
